import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import {
  TeamChannel,
  TeamChannelMember,
  TeamDMConversation,
  TeamMessage,
  TeamMessageReaction,
  TypingUser,
  MessageAttachment
} from '@/types/team-messaging';

// Types for state
interface ChannelMessagesState {
  messages: TeamMessage[];
  hasMore: boolean;
  isLoading: boolean;
}

interface DMMessagesState {
  messages: TeamMessage[];
  hasMore: boolean;
  isLoading: boolean;
}

interface UserPresenceState {
  userId: string;
  status: 'online' | 'away' | 'dnd' | 'offline';
  lastSeenAt?: string;
}

interface ThreadMessagesState {
  messages: TeamMessage[];
  hasMore: boolean;
  isLoading: boolean;
}

interface TeamMessagingState {
  // Channels
  channels: TeamChannel[];
  activeChannelId: string | null;
  channelMessages: Record<string, ChannelMessagesState>;
  channelMembers: Record<string, TeamChannelMember[]>;

  // DM Conversations
  dmConversations: TeamDMConversation[];
  activeDMId: string | null;
  dmMessages: Record<string, DMMessagesState>;

  // Threads
  threadMessages: Record<string, ThreadMessagesState>;
  activeThreadId: string | null;

  // Presence
  workspacePresence: Record<string, UserPresenceState>;

  // Typing indicators
  typingIndicators: TypingUser[];

  // Unread counts
  channelUnreadCounts: Record<string, number>;
  dmUnreadCounts: Record<string, number>;
  totalUnreadCount: number;

  // Loading states
  isLoadingChannels: boolean;
  isLoadingDMs: boolean;

  // Errors
  error: string | null;
}

const initialState: TeamMessagingState = {
  channels: [],
  activeChannelId: null,
  channelMessages: {},
  channelMembers: {},

  dmConversations: [],
  activeDMId: null,
  dmMessages: {},

  threadMessages: {},
  activeThreadId: null,

  workspacePresence: {},
  typingIndicators: [],

  channelUnreadCounts: {},
  dmUnreadCounts: {},
  totalUnreadCount: 0,

  isLoadingChannels: false,
  isLoadingDMs: false,
  error: null
};

// Async thunks
export const fetchChannels = createAsyncThunk(
  'teamMessaging/fetchChannels',
  async (workspaceId: string) => {
    const response = await fetch(`/api/team-messaging/channels?workspace_id=${workspaceId}`);
    if (!response.ok) throw new Error('Failed to fetch channels');
    const data = await response.json();
    return data.channels as TeamChannel[];
  }
);

export const fetchChannelMessages = createAsyncThunk(
  'teamMessaging/fetchChannelMessages',
  async ({ channelId, cursor }: { channelId: string; cursor?: string }) => {
    const url = cursor
      ? `/api/team-messaging/channels/${channelId}/messages?cursor=${cursor}`
      : `/api/team-messaging/channels/${channelId}/messages`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch messages');
    const data = await response.json();
    return { channelId, ...data };
  }
);

export const fetchDMConversations = createAsyncThunk(
  'teamMessaging/fetchDMConversations',
  async (workspaceId: string) => {
    const response = await fetch(`/api/team-messaging/dm?workspace_id=${workspaceId}`);
    if (!response.ok) throw new Error('Failed to fetch DM conversations');
    const data = await response.json();
    return data.conversations as TeamDMConversation[];
  }
);

export const fetchDMMessages = createAsyncThunk(
  'teamMessaging/fetchDMMessages',
  async ({ conversationId, cursor }: { conversationId: string; cursor?: string }) => {
    const url = cursor
      ? `/api/team-messaging/dm/${conversationId}/messages?cursor=${cursor}`
      : `/api/team-messaging/dm/${conversationId}/messages`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch messages');
    const data = await response.json();
    return { conversationId, ...data };
  }
);

export const sendChannelMessage = createAsyncThunk(
  'teamMessaging/sendChannelMessage',
  async ({ channelId, content, parentMessageId, attachments }: { channelId: string; content: string; parentMessageId?: string; attachments?: MessageAttachment[] }) => {
    const response = await fetch(`/api/team-messaging/channels/${channelId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, parent_message_id: parentMessageId, attachments })
    });
    if (!response.ok) throw new Error('Failed to send message');
    const data = await response.json();
    return { channelId, message: data.message as TeamMessage };
  }
);

export const sendDMMessage = createAsyncThunk(
  'teamMessaging/sendDMMessage',
  async ({ conversationId, content, parentMessageId, attachments }: { conversationId: string; content: string; parentMessageId?: string; attachments?: MessageAttachment[] }) => {
    const response = await fetch(`/api/team-messaging/dm/${conversationId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, parent_message_id: parentMessageId, attachments })
    });
    if (!response.ok) throw new Error('Failed to send message');
    const data = await response.json();
    return { conversationId, message: data.message as TeamMessage };
  }
);

export const createChannel = createAsyncThunk(
  'teamMessaging/createChannel',
  async ({ workspaceId, name, description, isPrivate }: { workspaceId: string; name: string; description?: string; isPrivate?: boolean }) => {
    const response = await fetch(`/api/team-messaging/channels?workspace_id=${workspaceId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, description, is_private: isPrivate })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create channel');
    }
    const data = await response.json();
    return data.channel as TeamChannel;
  }
);

export const createDMConversation = createAsyncThunk(
  'teamMessaging/createDMConversation',
  async ({ workspaceId, userIds, name, aiModel }: { workspaceId: string; userIds?: string[]; name?: string; aiModel?: string }) => {
    const body: Record<string, unknown> = {};
    if (aiModel) {
      body.ai_model = aiModel;
    } else {
      body.user_ids = userIds || [];
      if (name) body.name = name;
    }
    const response = await fetch(`/api/team-messaging/dm?workspace_id=${workspaceId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('Failed to create DM conversation');
    const data = await response.json();
    return data.conversation as TeamDMConversation;
  }
);

export const fetchWorkspacePresence = createAsyncThunk(
  'teamMessaging/fetchWorkspacePresence',
  async (workspaceId: string) => {
    const response = await fetch(`/api/team-messaging/presence?workspace_id=${workspaceId}`);
    if (!response.ok) throw new Error('Failed to fetch presence');
    const data = await response.json();
    return data.presence as Record<string, UserPresenceState>;
  }
);

export const addMessageReaction = createAsyncThunk(
  'teamMessaging/addMessageReaction',
  async ({ messageId, emoji }: { messageId: string; emoji: string }) => {
    const response = await fetch(`/api/team-messaging/messages/${messageId}/reactions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emoji })
    });
    if (!response.ok) throw new Error('Failed to add reaction');
    const data = await response.json();
    return { messageId, reaction: data.reaction as TeamMessageReaction };
  }
);

export const removeMessageReaction = createAsyncThunk(
  'teamMessaging/removeMessageReaction',
  async ({ messageId, emoji, userId }: { messageId: string; emoji: string; userId?: string }) => {
    const response = await fetch(`/api/team-messaging/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to remove reaction');
    return { messageId, emoji, userId };
  }
);

export const editMessage = createAsyncThunk(
  'teamMessaging/editMessage',
  async ({ messageId, content }: { messageId: string; content: string }) => {
    const response = await fetch(`/api/team-messaging/messages/${messageId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content })
    });
    if (!response.ok) throw new Error('Failed to edit message');
    const data = await response.json();
    return data.message as TeamMessage;
  }
);

export const deleteMessage = createAsyncThunk(
  'teamMessaging/deleteMessage',
  async ({ messageId }: { messageId: string }) => {
    const response = await fetch(`/api/team-messaging/messages/${messageId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete message');
    return { messageId };
  }
);

export const sendTypingIndicator = createAsyncThunk(
  'teamMessaging/sendTypingIndicator',
  async ({ channelId, dmConversationId, isTyping }: { channelId?: string; dmConversationId?: string; isTyping: boolean }) => {
    await fetch('/api/team-messaging/typing', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channelId, dmConversationId, isTyping })
    });
    return { channelId, dmConversationId, isTyping };
  }
);

// Thread thunks
export const fetchThreadReplies = createAsyncThunk(
  'teamMessaging/fetchThreadReplies',
  async ({ parentMessageId }: { parentMessageId: string }) => {
    const response = await fetch(`/api/team-messaging/messages/${parentMessageId}/thread`);
    if (!response.ok) throw new Error('Failed to fetch thread replies');
    const data = await response.json();
    return { parentMessageId, messages: data.replies as TeamMessage[], hasMore: data.hasMore };
  }
);

export const sendThreadReply = createAsyncThunk(
  'teamMessaging/sendThreadReply',
  async ({ parentMessageId, content, channelId, dmConversationId }: { parentMessageId: string; content: string; channelId?: string; dmConversationId?: string }) => {
    const body: any = { content, parent_message_id: parentMessageId };

    let url: string;
    if (channelId) {
      url = `/api/team-messaging/channels/${channelId}/messages`;
    } else if (dmConversationId) {
      url = `/api/team-messaging/dm/${dmConversationId}/messages`;
    } else {
      throw new Error('Either channelId or dmConversationId is required');
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('Failed to send reply');
    const data = await response.json();
    return { parentMessageId, message: data.message as TeamMessage };
  }
);

// Helper to find message in channel, DM, or a thread bucket. Thread replies
// live ONLY in state.threadMessages, so omitting them made edit/delete/reaction
// reducers silently no-op on thread replies.
const findMessageLocation = (
  state: TeamMessagingState,
  messageId: string
): { type: 'channel' | 'dm' | 'thread'; id: string } | null => {
  for (const [channelId, channelState] of Object.entries(state.channelMessages)) {
    if (channelState.messages.some(m => m.id === messageId)) {
      return { type: 'channel', id: channelId };
    }
  }
  for (const [dmId, dmState] of Object.entries(state.dmMessages)) {
    if (dmState.messages.some(m => m.id === messageId)) {
      return { type: 'dm', id: dmId };
    }
  }
  for (const [parentId, threadState] of Object.entries(state.threadMessages)) {
    if (threadState.messages.some(m => m.id === messageId)) {
      return { type: 'thread', id: parentId };
    }
  }
  return null;
};

// Resolve the mutable message array for a location (Immer draft).
const messagesForLocation = (
  state: TeamMessagingState,
  location: { type: 'channel' | 'dm' | 'thread'; id: string }
): TeamMessage[] | undefined => {
  if (location.type === 'channel') return state.channelMessages[location.id]?.messages;
  if (location.type === 'dm') return state.dmMessages[location.id]?.messages;
  return state.threadMessages[location.id]?.messages;
};

const teamMessagingSlice = createSlice({
  name: 'teamMessaging',
  initialState,
  reducers: {
    // Set active channel/DM
    setActiveChannel: (state, action: PayloadAction<string | null>) => {
      state.activeChannelId = action.payload;
      state.activeDMId = null;
      if (action.payload && state.channelUnreadCounts[action.payload]) {
        state.totalUnreadCount -= state.channelUnreadCounts[action.payload];
        state.channelUnreadCounts[action.payload] = 0;
      }
    },
    setActiveDM: (state, action: PayloadAction<string | null>) => {
      state.activeDMId = action.payload;
      state.activeChannelId = null;
      if (action.payload && state.dmUnreadCounts[action.payload]) {
        state.totalUnreadCount -= state.dmUnreadCounts[action.payload];
        state.dmUnreadCounts[action.payload] = 0;
      }
    },

    // Message operations (from WebSocket)
    addMessage: (state, action: PayloadAction<{ message: TeamMessage; channelId?: string; dmConversationId?: string }>) => {
      const { message, channelId, dmConversationId } = action.payload;
      const parentId = message.fk_parent_message_id;

      // Thread reply: route ONLY into the thread bucket (the main channel/DM
      // view fetches top-level messages, so thread replies must not be added
      // there or they appear duplicated in the main scroll).
      if (parentId) {
        if (!state.threadMessages[parentId]) {
          state.threadMessages[parentId] = { messages: [], hasMore: false, isLoading: false };
        }
        const isNewReply = !state.threadMessages[parentId].messages.some(m => m.id === message.id);
        if (isNewReply) {
          state.threadMessages[parentId].messages.push(message);
          // Bump _count.replies on the parent so the main view's "N replies"
          // badge updates without a refetch. Guarded by isNewReply: the same
          // reply can arrive via both WebSocket and the send thunk, and bumping
          // unconditionally double-counted it.
          const bumpReplies = (msgs: TeamMessage[]) => {
            const parent = msgs.find(m => m.id === parentId);
            if (parent) {
              if (!parent._count) parent._count = { replies: 0 };
              parent._count.replies = (parent._count.replies || 0) + 1;
            }
          };
          if (channelId && state.channelMessages[channelId]) bumpReplies(state.channelMessages[channelId].messages);
          if (dmConversationId && state.dmMessages[dmConversationId]) bumpReplies(state.dmMessages[dmConversationId].messages);
        }
        return;
      }

      // Top-level message: into the channel/DM bucket as before.
      if (channelId) {
        if (!state.channelMessages[channelId]) {
          state.channelMessages[channelId] = { messages: [], hasMore: false, isLoading: false };
        }
        // Avoid duplicates
        if (!state.channelMessages[channelId].messages.some(m => m.id === message.id)) {
          state.channelMessages[channelId].messages.push(message);
          // Increment unread if not active
          if (state.activeChannelId !== channelId) {
            state.channelUnreadCounts[channelId] = (state.channelUnreadCounts[channelId] || 0) + 1;
            state.totalUnreadCount += 1;
          }
        }
      }

      if (dmConversationId) {
        if (!state.dmMessages[dmConversationId]) {
          state.dmMessages[dmConversationId] = { messages: [], hasMore: false, isLoading: false };
        }
        if (!state.dmMessages[dmConversationId].messages.some(m => m.id === message.id)) {
          state.dmMessages[dmConversationId].messages.push(message);
          if (state.activeDMId !== dmConversationId) {
            state.dmUnreadCounts[dmConversationId] = (state.dmUnreadCounts[dmConversationId] || 0) + 1;
            state.totalUnreadCount += 1;
          }
        }
      }
    },

    updateMessage: (state, action: PayloadAction<{ message: TeamMessage; channelId?: string; dmConversationId?: string }>) => {
      const { message, channelId, dmConversationId } = action.payload;

      if (channelId && state.channelMessages[channelId]) {
        const idx = state.channelMessages[channelId].messages.findIndex(m => m.id === message.id);
        if (idx !== -1) {
          state.channelMessages[channelId].messages[idx] = message;
        }
      }

      if (dmConversationId && state.dmMessages[dmConversationId]) {
        const idx = state.dmMessages[dmConversationId].messages.findIndex(m => m.id === message.id);
        if (idx !== -1) {
          state.dmMessages[dmConversationId].messages[idx] = message;
        }
      }

      // Also walk every thread bucket — when an AI placeholder posted into a
      // thread is later UPDATED with the final reply, the thread panel must
      // see the new content/streaming_status, not the empty placeholder.
      for (const tb of Object.values(state.threadMessages)) {
        const idx = tb.messages.findIndex(m => m.id === message.id);
        if (idx !== -1) {
          tb.messages[idx] = message;
        }
      }
    },

    removeMessage: (state, action: PayloadAction<{ messageId: string; channelId?: string; dmConversationId?: string }>) => {
      const { messageId, channelId, dmConversationId } = action.payload;

      if (channelId && state.channelMessages[channelId]) {
        state.channelMessages[channelId].messages = state.channelMessages[channelId].messages.filter(m => m.id !== messageId);
      }

      if (dmConversationId && state.dmMessages[dmConversationId]) {
        state.dmMessages[dmConversationId].messages = state.dmMessages[dmConversationId].messages.filter(m => m.id !== messageId);
      }
    },

    // AI DM streaming: append a chunk to a placeholder message that is currently
    // being streamed from tamer-service. The placeholder may live in the dm
    // bucket (top-level reply) AND/OR in a thread bucket (when the user
    // replied inside a thread). Walk both so the chunk lands wherever the UI
    // is looking.
    appendStreamingChunk: (state, action: PayloadAction<{ dmConversationId: string; messageId: string; chunk: string }>) => {
      const { dmConversationId, messageId, chunk } = action.payload;
      const dmBucket = state.dmMessages[dmConversationId];
      const dmMsg = dmBucket?.messages.find(m => m.id === messageId);
      if (dmMsg) {
        dmMsg.content = (dmMsg.content || '') + chunk;
        dmMsg.streaming_status = 'streaming';
      }
      // Look in thread buckets too — same message id may appear in both.
      for (const tb of Object.values(state.threadMessages)) {
        const tm = tb.messages.find(m => m.id === messageId);
        if (tm) {
          tm.content = (tm.content || '') + chunk;
          tm.streaming_status = 'streaming';
        }
      }
    },

    finalizeStreamingMessage: (state, action: PayloadAction<{ dmConversationId: string; messageId: string; content?: string; status?: 'complete' | 'error' }>) => {
      const { dmConversationId, messageId, content, status } = action.payload;
      const dmBucket = state.dmMessages[dmConversationId];
      const dmMsg = dmBucket?.messages.find(m => m.id === messageId);
      if (dmMsg) {
        if (typeof content === 'string') dmMsg.content = content;
        dmMsg.streaming_status = status ?? 'complete';
      }
      for (const tb of Object.values(state.threadMessages)) {
        const tm = tb.messages.find(m => m.id === messageId);
        if (tm) {
          if (typeof content === 'string') tm.content = content;
          tm.streaming_status = status ?? 'complete';
        }
      }
    },

    // Reaction operations
    addReaction: (state, action: PayloadAction<{ reaction: TeamMessageReaction; messageId: string; channelId?: string; dmConversationId?: string }>) => {
      const { reaction, messageId, channelId, dmConversationId } = action.payload;

      const updateMessageReactions = (messages: TeamMessage[]) => {
        const msg = messages.find(m => m.id === messageId);
        if (msg) {
          if (!msg.reactions) msg.reactions = [];
          if (!msg.reactions.some(r => r.id === reaction.id)) {
            msg.reactions.push(reaction);
          }
        }
      };

      if (channelId && state.channelMessages[channelId]) {
        updateMessageReactions(state.channelMessages[channelId].messages);
      }
      if (dmConversationId && state.dmMessages[dmConversationId]) {
        updateMessageReactions(state.dmMessages[dmConversationId].messages);
      }
    },

    removeReaction: (state, action: PayloadAction<{ reaction: TeamMessageReaction; messageId: string; channelId?: string; dmConversationId?: string }>) => {
      const { reaction, messageId, channelId, dmConversationId } = action.payload;

      const updateMessageReactions = (messages: TeamMessage[]) => {
        const msg = messages.find(m => m.id === messageId);
        if (msg && msg.reactions) {
          msg.reactions = msg.reactions.filter(r => !(r.fk_user_id === reaction.fk_user_id && r.emoji === reaction.emoji));
        }
      };

      if (channelId && state.channelMessages[channelId]) {
        updateMessageReactions(state.channelMessages[channelId].messages);
      }
      if (dmConversationId && state.dmMessages[dmConversationId]) {
        updateMessageReactions(state.dmMessages[dmConversationId].messages);
      }
    },

    // Channel operations
    addChannel: (state, action: PayloadAction<TeamChannel>) => {
      if (!state.channels.some(c => c.id === action.payload.id)) {
        state.channels.push(action.payload);
        state.channels.sort((a, b) => a.name.localeCompare(b.name));
      }
    },

    updateChannel: (state, action: PayloadAction<TeamChannel>) => {
      const idx = state.channels.findIndex(c => c.id === action.payload.id);
      if (idx !== -1) {
        state.channels[idx] = { ...state.channels[idx], ...action.payload };
      }
    },

    removeChannel: (state, action: PayloadAction<string>) => {
      state.channels = state.channels.filter(c => c.id !== action.payload);
      if (state.activeChannelId === action.payload) {
        state.activeChannelId = null;
      }
      delete state.channelMessages[action.payload];
      delete state.channelMembers[action.payload];
      // Subtract this channel's unread from the global badge before dropping it,
      // otherwise totalUnreadCount drifts upward (badge stuck non-zero).
      const removedUnread = state.channelUnreadCounts[action.payload] || 0;
      state.totalUnreadCount = Math.max(0, state.totalUnreadCount - removedUnread);
      delete state.channelUnreadCounts[action.payload];
    },

    // Channel member operations
    addChannelMember: (state, action: PayloadAction<{ member: TeamChannelMember; channelId: string }>) => {
      const { member, channelId } = action.payload;
      if (!state.channelMembers[channelId]) {
        state.channelMembers[channelId] = [];
      }
      if (!state.channelMembers[channelId].some(m => m.id === member.id)) {
        state.channelMembers[channelId].push(member);
      }
    },

    removeChannelMember: (state, action: PayloadAction<{ userId: string; channelId: string }>) => {
      const { userId, channelId } = action.payload;
      if (state.channelMembers[channelId]) {
        state.channelMembers[channelId] = state.channelMembers[channelId].filter(m => m.fk_user_id !== userId);
      }
    },

    // DM operations
    addDMConversation: (state, action: PayloadAction<TeamDMConversation>) => {
      if (!state.dmConversations.some(c => c.id === action.payload.id)) {
        state.dmConversations.unshift(action.payload);
      }
    },

    // Presence operations
    updateUserPresence: (state, action: PayloadAction<{ userId: string; status: string; lastSeenAt?: string }>) => {
      const { userId, status, lastSeenAt } = action.payload;
      state.workspacePresence[userId] = {
        userId,
        status: status as 'online' | 'away' | 'dnd' | 'offline',
        lastSeenAt
      };
    },

    // Typing indicators
    setUserTyping: (state, action: PayloadAction<TypingUser>) => {
      const { userId, channelId, dmConversationId } = action.payload;
      // Remove any existing typing indicator for this user in this context
      state.typingIndicators = state.typingIndicators.filter(
        t => !(t.userId === userId && t.channelId === channelId && t.dmConversationId === dmConversationId)
      );
      state.typingIndicators.push(action.payload);
    },

    clearUserTyping: (state, action: PayloadAction<{ userId: string; channelId?: string; dmConversationId?: string }>) => {
      const { userId, channelId, dmConversationId } = action.payload;
      state.typingIndicators = state.typingIndicators.filter(
        t => !(t.userId === userId && t.channelId === channelId && t.dmConversationId === dmConversationId)
      );
    },

    // Clean expired typing indicators (call this periodically)
    cleanExpiredTypingIndicators: (state) => {
      const now = Date.now();
      const TYPING_TIMEOUT = 5000; // 5 seconds
      state.typingIndicators = state.typingIndicators.filter(
        t => now - t.timestamp < TYPING_TIMEOUT
      );
    },

    // Thread operations
    setActiveThread: (state, action: PayloadAction<string | null>) => {
      state.activeThreadId = action.payload;
    },

    addThreadReply: (state, action: PayloadAction<{ parentMessageId: string; message: TeamMessage }>) => {
      const { parentMessageId, message } = action.payload;
      if (!state.threadMessages[parentMessageId]) {
        state.threadMessages[parentMessageId] = { messages: [], hasMore: false, isLoading: false };
      }
      if (!state.threadMessages[parentMessageId].messages.some(m => m.id === message.id)) {
        state.threadMessages[parentMessageId].messages.push(message);
      }
    },

    // Clear all state (on logout or workspace switch)
    clearTeamMessagingState: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    // Fetch channels
    builder.addCase(fetchChannels.pending, (state) => {
      state.isLoadingChannels = true;
      state.error = null;
    });
    builder.addCase(fetchChannels.fulfilled, (state, action) => {
      state.isLoadingChannels = false;
      state.channels = action.payload;
    });
    builder.addCase(fetchChannels.rejected, (state, action) => {
      state.isLoadingChannels = false;
      state.error = action.error.message || 'Failed to fetch channels';
    });

    // Fetch channel messages
    builder.addCase(fetchChannelMessages.pending, (state, action) => {
      const channelId = action.meta.arg.channelId;
      if (!state.channelMessages[channelId]) {
        state.channelMessages[channelId] = { messages: [], hasMore: false, isLoading: true };
      } else {
        state.channelMessages[channelId].isLoading = true;
      }
    });
    builder.addCase(fetchChannelMessages.fulfilled, (state, action) => {
      const { channelId, messages, hasMore } = action.payload;
      const cursor = action.meta.arg.cursor;

      if (!state.channelMessages[channelId]) {
        state.channelMessages[channelId] = { messages: [], hasMore: false, isLoading: false };
      }

      if (cursor) {
        // Prepend older messages
        state.channelMessages[channelId].messages = [...messages, ...state.channelMessages[channelId].messages];
      } else {
        // Initial load: MERGE rather than replace, so messages that arrived via
        // WebSocket while this fetch was in flight aren't dropped.
        const fetchedIds = new Set(messages.map((m: TeamMessage) => m.id));
        const extra = state.channelMessages[channelId].messages.filter((m: TeamMessage) => !fetchedIds.has(m.id));
        const merged = [...messages, ...extra];
        merged.sort((a: TeamMessage, b: TeamMessage) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        state.channelMessages[channelId].messages = merged;
      }

      state.channelMessages[channelId].hasMore = hasMore;
      state.channelMessages[channelId].isLoading = false;
    });
    builder.addCase(fetchChannelMessages.rejected, (state, action) => {
      const channelId = action.meta.arg.channelId;
      if (state.channelMessages[channelId]) {
        state.channelMessages[channelId].isLoading = false;
      }
      state.error = action.error.message || 'Failed to fetch messages';
    });

    // Fetch DM conversations
    builder.addCase(fetchDMConversations.pending, (state) => {
      state.isLoadingDMs = true;
      state.error = null;
    });
    builder.addCase(fetchDMConversations.fulfilled, (state, action) => {
      state.isLoadingDMs = false;
      state.dmConversations = action.payload;
    });
    builder.addCase(fetchDMConversations.rejected, (state, action) => {
      state.isLoadingDMs = false;
      state.error = action.error.message || 'Failed to fetch DM conversations';
    });

    // Fetch DM messages
    builder.addCase(fetchDMMessages.pending, (state, action) => {
      const conversationId = action.meta.arg.conversationId;
      if (!state.dmMessages[conversationId]) {
        state.dmMessages[conversationId] = { messages: [], hasMore: false, isLoading: true };
      } else {
        state.dmMessages[conversationId].isLoading = true;
      }
    });
    builder.addCase(fetchDMMessages.fulfilled, (state, action) => {
      const { conversationId, messages, hasMore } = action.payload;
      const cursor = action.meta.arg.cursor;

      if (!state.dmMessages[conversationId]) {
        state.dmMessages[conversationId] = { messages: [], hasMore: false, isLoading: false };
      }

      if (cursor) {
        state.dmMessages[conversationId].messages = [...messages, ...state.dmMessages[conversationId].messages];
      } else {
        // Initial load: MERGE (see channel branch) so in-flight WebSocket
        // messages aren't dropped.
        const fetchedIds = new Set(messages.map((m: TeamMessage) => m.id));
        const extra = state.dmMessages[conversationId].messages.filter((m: TeamMessage) => !fetchedIds.has(m.id));
        const merged = [...messages, ...extra];
        merged.sort((a: TeamMessage, b: TeamMessage) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        state.dmMessages[conversationId].messages = merged;
      }

      state.dmMessages[conversationId].hasMore = hasMore;
      state.dmMessages[conversationId].isLoading = false;
    });
    builder.addCase(fetchDMMessages.rejected, (state, action) => {
      const conversationId = action.meta.arg.conversationId;
      if (state.dmMessages[conversationId]) {
        state.dmMessages[conversationId].isLoading = false;
      }
      state.error = action.error.message || 'Failed to fetch messages';
    });

    // Send channel message
    builder.addCase(sendChannelMessage.fulfilled, (state, action) => {
      const { channelId, message } = action.payload;
      if (!state.channelMessages[channelId]) {
        state.channelMessages[channelId] = { messages: [], hasMore: false, isLoading: false };
      }
      // Avoid duplicates (message may arrive via WebSocket first)
      if (!state.channelMessages[channelId].messages.some(m => m.id === message.id)) {
        state.channelMessages[channelId].messages.push(message);
      }
    });

    // Send DM message
    builder.addCase(sendDMMessage.fulfilled, (state, action) => {
      const { conversationId, message } = action.payload;
      if (!state.dmMessages[conversationId]) {
        state.dmMessages[conversationId] = { messages: [], hasMore: false, isLoading: false };
      }
      if (!state.dmMessages[conversationId].messages.some(m => m.id === message.id)) {
        state.dmMessages[conversationId].messages.push(message);
      }
    });

    // Create channel
    builder.addCase(createChannel.fulfilled, (state, action) => {
      if (!state.channels.some(c => c.id === action.payload.id)) {
        state.channels.push(action.payload);
        state.channels.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    // Create DM conversation
    builder.addCase(createDMConversation.fulfilled, (state, action) => {
      if (!state.dmConversations.some(c => c.id === action.payload.id)) {
        state.dmConversations.unshift(action.payload);
      }
    });

    // Fetch workspace presence
    builder.addCase(fetchWorkspacePresence.fulfilled, (state, action) => {
      state.workspacePresence = action.payload;
    });

    // Add message reaction
    builder.addCase(addMessageReaction.fulfilled, (state, action) => {
      const { messageId, reaction } = action.payload;
      const location = findMessageLocation(state, messageId);
      const msg = location ? messagesForLocation(state, location)?.find(m => m.id === messageId) : undefined;
      if (msg) {
        if (!msg.reactions) msg.reactions = [];
        if (!msg.reactions.some(r => r.id === reaction.id)) {
          msg.reactions.push(reaction);
        }
      }
    });

    // Remove message reaction
    builder.addCase(removeMessageReaction.fulfilled, (state, action) => {
      const { messageId, emoji, userId } = action.payload;
      // Remove ONLY the current user's reaction with this emoji. Filtering by
      // emoji alone wiped every user's reaction of that emoji from the UI.
      const drop = (r: { emoji: string; fk_user_id?: string }) =>
        r.emoji === emoji && (!userId || r.fk_user_id === userId);
      const location = findMessageLocation(state, messageId);
      const msg = location ? messagesForLocation(state, location)?.find(m => m.id === messageId) : undefined;
      if (msg && msg.reactions) {
        msg.reactions = msg.reactions.filter(r => !drop(r));
      }
    });

    // Edit message
    builder.addCase(editMessage.fulfilled, (state, action) => {
      const updatedMessage = action.payload;
      const location = findMessageLocation(state, updatedMessage.id);
      const msgs = location ? messagesForLocation(state, location) : undefined;
      const msg = msgs?.find(m => m.id === updatedMessage.id);
      if (msg) {
        msg.content = updatedMessage.content;
        msg.is_edited = true;
      }
    });

    // Delete message
    builder.addCase(deleteMessage.fulfilled, (state, action) => {
      const { messageId } = action.payload;
      const location = findMessageLocation(state, messageId);
      const msg = location ? messagesForLocation(state, location)?.find(m => m.id === messageId) : undefined;
      if (msg) {
        msg.is_deleted = true;
        // If this was a thread reply, decrement the parent's reply badge so it
        // stays in sync with the non-deleted reply count.
        const parentId = msg.fk_parent_message_id;
        if (parentId) {
          for (const bucket of [...Object.values(state.channelMessages), ...Object.values(state.dmMessages)]) {
            const parent = bucket.messages.find(m => m.id === parentId);
            if (parent?._count) {
              parent._count.replies = Math.max(0, (parent._count.replies || 0) - 1);
              break;
            }
          }
        }
      }
    });

    // Fetch thread replies
    builder.addCase(fetchThreadReplies.pending, (state, action) => {
      const parentMessageId = action.meta.arg.parentMessageId;
      if (!state.threadMessages[parentMessageId]) {
        state.threadMessages[parentMessageId] = { messages: [], hasMore: false, isLoading: true };
      } else {
        state.threadMessages[parentMessageId].isLoading = true;
      }
    });
    builder.addCase(fetchThreadReplies.fulfilled, (state, action) => {
      const { parentMessageId, messages, hasMore } = action.payload;
      if (!state.threadMessages[parentMessageId]) {
        state.threadMessages[parentMessageId] = { messages: [], hasMore: false, isLoading: false };
      }
      state.threadMessages[parentMessageId].messages = messages;
      state.threadMessages[parentMessageId].hasMore = hasMore;
      state.threadMessages[parentMessageId].isLoading = false;
    });
    builder.addCase(fetchThreadReplies.rejected, (state, action) => {
      const parentMessageId = action.meta.arg.parentMessageId;
      if (state.threadMessages[parentMessageId]) {
        state.threadMessages[parentMessageId].isLoading = false;
      }
    });

    // Send thread reply
    builder.addCase(sendThreadReply.fulfilled, (state, action) => {
      const { parentMessageId, message } = action.payload;
      if (!state.threadMessages[parentMessageId]) {
        state.threadMessages[parentMessageId] = { messages: [], hasMore: false, isLoading: false };
      }
      if (!state.threadMessages[parentMessageId].messages.some(m => m.id === message.id)) {
        state.threadMessages[parentMessageId].messages.push(message);
      }
      // Also update the reply count on the parent message
      const location = findMessageLocation(state, parentMessageId);
      if (location) {
        if (location.type === 'channel' && state.channelMessages[location.id]) {
          const parentMsg = state.channelMessages[location.id].messages.find(m => m.id === parentMessageId);
          if (parentMsg) {
            if (!parentMsg._count) parentMsg._count = { replies: 0 };
            parentMsg._count.replies = (parentMsg._count.replies || 0) + 1;
          }
        } else if (location.type === 'dm' && state.dmMessages[location.id]) {
          const parentMsg = state.dmMessages[location.id].messages.find(m => m.id === parentMessageId);
          if (parentMsg) {
            if (!parentMsg._count) parentMsg._count = { replies: 0 };
            parentMsg._count.replies = (parentMsg._count.replies || 0) + 1;
          }
        }
      }
    });
  }
});

export const {
  setActiveChannel,
  setActiveDM,
  addMessage,
  updateMessage,
  removeMessage,
  appendStreamingChunk,
  finalizeStreamingMessage,
  addReaction,
  removeReaction,
  addChannel,
  updateChannel,
  removeChannel,
  addChannelMember,
  removeChannelMember,
  addDMConversation,
  updateUserPresence,
  setUserTyping,
  clearUserTyping,
  cleanExpiredTypingIndicators,
  setActiveThread,
  addThreadReply,
  clearTeamMessagingState
} = teamMessagingSlice.actions;

// Selectors
export const selectChannels = (state: RootState) => state.teamMessaging.channels;
export const selectActiveChannelId = (state: RootState) => state.teamMessaging.activeChannelId;
export const selectActiveChannel = (state: RootState) =>
  state.teamMessaging.channels.find(c => c.id === state.teamMessaging.activeChannelId);
// Stable empty reference so a cache miss doesn't return a new [] each call
// (which makes useSelector re-render every dispatch).
const EMPTY_MESSAGES: TeamMessage[] = [];
export const selectChannelMessages = (channelId: string) => (state: RootState) =>
  state.teamMessaging.channelMessages[channelId]?.messages || EMPTY_MESSAGES;
export const selectChannelMessagesState = (channelId: string) => (state: RootState) =>
  state.teamMessaging.channelMessages[channelId];

export const selectDMConversations = (state: RootState) => state.teamMessaging.dmConversations;
export const selectActiveDMId = (state: RootState) => state.teamMessaging.activeDMId;
export const selectActiveDM = (state: RootState) =>
  state.teamMessaging.dmConversations.find(c => c.id === state.teamMessaging.activeDMId);
export const selectDMMessages = (conversationId: string) => (state: RootState) =>
  state.teamMessaging.dmMessages[conversationId]?.messages || EMPTY_MESSAGES;
export const selectDMMessagesState = (conversationId: string) => (state: RootState) =>
  state.teamMessaging.dmMessages[conversationId];

export const selectWorkspacePresence = (state: RootState) => state.teamMessaging.workspacePresence;
export const selectUserPresence = (userId: string) => (state: RootState) =>
  state.teamMessaging.workspacePresence[userId];

export const selectTypingIndicators = (state: RootState) => state.teamMessaging.typingIndicators;
export const selectChannelTypingUsers = (channelId: string) => (state: RootState) =>
  state.teamMessaging.typingIndicators.filter(t => t.channelId === channelId);
export const selectDMTypingUsers = (conversationId: string) => (state: RootState) =>
  state.teamMessaging.typingIndicators.filter(t => t.dmConversationId === conversationId);

export const selectTotalUnreadCount = (state: RootState) => state.teamMessaging.totalUnreadCount;
export const selectChannelUnreadCount = (channelId: string) => (state: RootState) =>
  state.teamMessaging.channelUnreadCounts[channelId] || 0;
export const selectDMUnreadCount = (conversationId: string) => (state: RootState) =>
  state.teamMessaging.dmUnreadCounts[conversationId] || 0;

export const selectIsLoadingChannels = (state: RootState) => state.teamMessaging.isLoadingChannels;
export const selectIsLoadingDMs = (state: RootState) => state.teamMessaging.isLoadingDMs;

// Thread selectors
export const selectActiveThreadId = (state: RootState) => state.teamMessaging.activeThreadId;
export const selectThreadMessages = (parentMessageId: string) => (state: RootState) =>
  state.teamMessaging.threadMessages[parentMessageId];

export default teamMessagingSlice.reducer;
